import {
  CapitalAccountsNumbersAggregationChart,
  Dropdown,
  NavTotals,
} from '../../../../components';
import { type SelectOption } from '../../../../hooks';
import {
  type CapitalAccountsStoreState,
  getInitialTableColumnDisplayValues,
  type OrganizationsStoreState,
  selectAccountForFund,
  selectPeriodForAccount,
} from '../../../../store/slices';
import {
  type Account,
  type CapitalAccountPeriodInformation,
  type TableColumnDisplayValues,
} from '../../../../types';
import { CombinedContributionCommitmentChart } from 'components/Charts/CombinedContributionCommitmentChart/CombinedContributionCommitmentChart';
import { limitString } from 'components/Pipes/textPipes';
import { type FC, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isMobile, isTablet } from 'services/mobile';
import { COLORS } from 'types/Colors';

export type CapitalAccountsDetailsProps = {
  accountId: string,
  accounts: Account[],
  fundId: string,
};

type AccountWithPeriods = Account & {
  periods: Record<string, CapitalAccountPeriodInformation>,
};

export const CapitalAccountsDetails: FC<CapitalAccountsDetailsProps> = ({
  accountId,
  accounts,
  fundId,
}) => {
  const dispatch = useDispatch();
  const { displayedOrganization } = useSelector<{
    organizations: OrganizationsStoreState,
  }>((state) => state.organizations) as OrganizationsStoreState;
  const accountsWithPeriods = accounts as AccountWithPeriods[];

  const accountOptions: SelectOption[] = accounts.map((acc) => ({
    display: isMobile() ? limitString(acc.legalName, 10) : acc.legalName,
    id: acc._id,
  }));

  const initialSelectedAccount = accountOptions.find((acc) => acc.id === accountId) ?? accountOptions[0];
  const initialPeriods: SelectOption[] = useMemo(() => {
    const selectedAccountPeriods = accountsWithPeriods.find((a) => a._id === accountId)?.periods ?? {};
    return Object.keys(selectedAccountPeriods).map((period) => ({
      display: period,
      id: period,
    }));
  }, [accountId, accountsWithPeriods]);

  const [selectedAccount, setSelectedAccount] = useState<SelectOption>(initialSelectedAccount);
  const [selectedPeriod, setSelectedPeriod] = useState<SelectOption>(initialPeriods[0]);

  const fundData = useSelector((state: { capitalAccounts: CapitalAccountsStoreState, }) =>
    state.capitalAccounts.computedCapitalAccountsData?.find((fun) => fun._id === fundId),
  );
  const computedAccounts = fundData?.accounts ?? [];
  const computedAccount = computedAccounts.find((a) => a._id === selectedAccount.id);
  const displayInfo: TableColumnDisplayValues = computedAccount?.periods?.[selectedPeriod.id] ??
    getInitialTableColumnDisplayValues(selectedAccount.id);

  const capitalAccountInfo =
    accountsWithPeriods.find((a) => a._id === selectedAccount.id)?.periods?.[selectedPeriod.id];

  const onSelectAccount = (newAccount: SelectOption) => {
    setSelectedAccount(newAccount);
    dispatch(selectAccountForFund({ accountId: newAccount.id, fundId }));

    const newAccountPeriods = accountsWithPeriods.find((a) => a._id === newAccount.id)?.periods;
    const firstPeriodKey = newAccountPeriods ? Object.keys(newAccountPeriods)[0] : '';
    if (firstPeriodKey) {
      const newPeriod: SelectOption = { display: firstPeriodKey, id: firstPeriodKey };
      setSelectedPeriod(newPeriod);
      dispatch(selectPeriodForAccount({ accountId: newAccount.id, fundId, period: firstPeriodKey }));
    }
  };

  const onSelectPeriod = (newPeriod: SelectOption) => {
    setSelectedPeriod(newPeriod);
    dispatch(selectPeriodForAccount({
      accountId: selectedAccount.id,
      fundId,
      period: newPeriod.id,
    }));
  };

  const currentPeriodOptions = useMemo(() => {
    const selectedAccountPeriods = accountsWithPeriods.find((a) => a._id === selectedAccount.id)?.periods ?? {};
    return Object.keys(selectedAccountPeriods).map((period) => ({
      display: period,
      id: period,
    }));
  }, [accountsWithPeriods, selectedAccount]);

  return (
    <>
      <div>
        <div className='flex flex-col items-center justify-between gap-[0.5rem] ms:flex-row'>
          <div className='flex items-center gap-[0.75rem] self-start '>
            {isTablet() ? null : <img
              alt='orgLogo'
              className='max-h-8 max-w-8'
              src={displayedOrganization?.organizationLogo}
            />}
            <div className='flex items-center gap-[0.5rem]'>
              <Dropdown
                className='!w-fit'
                dataSelectionValue='ca-account-selector-dropdown'
                displayColor={COLORS['$main-700']}
                displayFontWeight='500'
                displaySize='!text-title-4'
                optionList={accountOptions}
                optionTextStyle='!text-font-2'
                placeholder='Account'
                setValue={onSelectAccount}
                type='transparent'
                value={selectedAccount}
                withAltDesign
              />
              <span className='text-nowrap text-title-4 font-medium'>as of</span>
              <Dropdown
                className='!w-fit'
                dataSelectionValue='ca-fund-selector-dropdown'
                displayColor={COLORS['$main-700']}
                displayFontWeight='500'
                displaySize='!text-title-4'
                optionList={currentPeriodOptions}
                optionTextStyle='!text-font-2'
                placeholder='Period'
                setValue={onSelectPeriod}
                type='transparent'
                value={selectedPeriod}
                withAltDesign
              />
            </div>
          </div>
          {/* this button gonna be in the next version */}
          {/* <Button
            className='w-max !rounded-[0.5rem]'
            data-test='download-file'
            icon='left'
            iconid='download-excel-1f'
            // isLoading={}
            onClick={() => ({})}
            size='medium'
            squared
            tooltipWrapperClass='!w-fit self-end ms:self-center'
            type='main'
          >Download</Button> */}
        </div>
      </div>
      <CapitalAccountsNumbersAggregationChart values={displayInfo} />
      <div className='flex flex-col gap-[1rem] ms:flex-row'>
        <div className='flex w-full flex-col gap-[1rem] ms:w-1/2'>
          <div className='flex flex-col flex-wrap gap-[1rem] overflow-hidden rounded-medium bg-white-999 p-[1rem] shadow-card-x3 dt:flex-row ms:p-[1.25rem]'>
            <CombinedContributionCommitmentChart
              commitmentValues={displayInfo}
              contributionValues={displayInfo}
            />
          </div>
        </div>
        <div className='flex w-full flex-col ms:w-1/2'>
          {capitalAccountInfo && displayInfo &&
            <NavTotals
              {...displayInfo}
              {...capitalAccountInfo}
            />
          }
        </div>
      </div>
    </>
  );
};
